/**
 * Transaction Detail Sheet — slideout panel for a single bank transaction.
 *
 * Shows payment details, editable agent category + agent counterparty,
 * source document link, notes, and change history.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/ui/primitives/select";
import { Button } from "@/ui/primitives/button";
import { DocumentDetail } from "../../documents/DocumentDetail";
import { ChangeHistory } from "../shared-panels";
import type { ResolvedHistoryUser } from "../shared-panels/ChangeHistory";
import {
  ConfirmEditDialog,
  type PendingEdit,
} from "../shared-panels/ConfirmEditDialog";
import { VouchBadge } from "./badges";
import { VOUCH_CONFIG } from "./badges.helpers";
import type {
  VouchStatus,
  LedgerCharacterisation,
} from "@cvx/jobs/summarization/agents/bankTransactions/vouchCore";
import {
  BANK_TRANSACTION_FIELD_FORMATTERS,
  BANK_TRANSACTION_FIELD_LABELS,
} from "./historyFormatters";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BankTxnEntry {
  _id: string;
  date: string;
  amount: number;
  direction: "debit" | "credit";
  description: string;
  counterpartyName?: string;
  extractionCounterpartyName?: string;
  reference?: string;
  balanceAfter?: number;
  accountInstitution?: string;
  accountNumber?: string;
  transactionType: string;
  extractionTransactionType?: string;
  typeSource: string;
  sourceDocumentId?: string;
  sourceFileName?: string;
  notes?: string;
  isManualOverride?: boolean;
  vouchStatus?: VouchStatus;
  ledgerCharacterisation?: LedgerCharacterisation;
  vouchOverride?: {
    action: "force_vouched" | "force_unmatched";
    reason?: string;
  };
  changeHistory: Array<{
    timestamp: number;
    actor: string;
    userId?: Id<"users">;
    action: string;
    reason: string;
    previousValues?: Record<string, unknown>;
    newValues?: Record<string, unknown>;
  }>;
}

const TRANSACTION_TYPES = [
  { value: "employee", label: "Possible Employee" },
  { value: "trade_third_parties", label: "Trade & Third Parties" },
  { value: "government_statutory", label: "Government & Statutory" },
  { value: "financial_institutions", label: "Financial Institutions" },
  { value: "utilities_rent", label: "Utilities & Rent" },
  { value: "internal_intercompany", label: "Internal/Inter-company" },
  { value: "professional_advisors", label: "Professional Advisors" },
  { value: "software_subscriptions", label: "Software & Subscriptions" },
  { value: "insurance", label: "Insurance" },
  { value: "fuel", label: "Fuel" },
  { value: "retail", label: "Retail" },
  { value: "travel", label: "Travel" },
  { value: "unknown", label: "Unknown" },
];

const TYPE_BADGE_COLOR: Record<
  string,
  | "purple"
  | "blue"
  | "teal"
  | "indigo"
  | "sky"
  | "orange"
  | "pink"
  | "red"
  | "gray"
  | "emerald"
> = {
  employee: "purple",
  trade_third_parties: "blue",
  government_statutory: "teal",
  financial_institutions: "indigo",
  utilities_rent: "sky",
  internal_intercompany: "orange",
  professional_advisors: "pink",
  software_subscriptions: "blue",
  insurance: "teal",
  fuel: "orange",
  retail: "indigo",
  travel: "emerald",
  related_party: "red",
  unknown: "gray",
};

const fmt = (n: number) =>
  "$" + n.toLocaleString("en-AU", { minimumFractionDigits: 0 });

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TransactionDetailSheetProps {
  txn: BankTxnEntry;
  jobId?: Id<"jobs">;
  onClose: () => void;
  isViewer?: boolean;
  vouchingActive?: boolean;
}

export function TransactionDetailSheet({
  txn: txnProp,
  jobId,
  onClose,
  isViewer,
  vouchingActive,
}: TransactionDetailSheetProps) {
  // Live query so edits are reflected immediately
  const { data: liveEntry } = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.getBankTransactionEntry, {
      entryId: txnProp._id as Id<"bankTransactionEntries">,
    }),
  );
  const txn = (liveEntry as BankTxnEntry | undefined) ?? txnProp;

  // Vouch status / ledger characterisation are computed client-side in
  // useAllBankTransactions and arrive only on the passed-in prop — the live
  // entry query returns the raw row without them.
  const vouchStatus = txnProp.vouchStatus;
  const ledgerCharacterisation = txnProp.ledgerCharacterisation;
  const vouchOverride = txn.vouchOverride;

  const [viewingDocumentId, setViewingDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [counterpartyInput, setCounterpartyInput] = useState(
    txn.counterpartyName ?? "",
  );
  // Edit awaiting practitioner confirmation (all inline edits pass through
  // the shared ConfirmEditDialog before any mutation fires).
  const [pendingEdit, setPendingEdit] = useState<PendingEdit | null>(null);

  // `mutateAsync` so the ConfirmEditDialog can await the write and keep the
  // staged edit on failure.
  const { mutateAsync: updateField } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .updateTransactionField,
    ),
  });

  const { mutate: overrideVouch } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.bankTransactions.publicMutations
        .overrideVouchStatus,
    ),
  });

  const handleVouchOverride = useCallback(
    (action: "force_vouched" | "force_unmatched" | "clear") => {
      if (isViewer || !jobId) return;
      overrideVouch({
        jobId,
        entryId: txn._id as Id<"bankTransactionEntries">,
        action,
      });
    },
    [isViewer, jobId, overrideVouch, txn._id],
  );

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Sync counterparty input when txn changes
  useEffect(() => {
    setCounterpartyInput(txn.counterpartyName ?? "");
  }, [txn.counterpartyName]);

  const historyUserIds = useMemo<Array<Id<"users">>>(() => {
    if (!txn.changeHistory) return [];
    const ids = new Set<Id<"users">>();
    for (const change of txn.changeHistory) {
      if (change.userId) ids.add(change.userId);
    }
    return Array.from(ids);
  }, [txn.changeHistory]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, {
      ids: historyUserIds,
    }),
    enabled: historyUserIds.length > 0,
  });

  const historyUsersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) {
      map.set(u._id, u);
    }
    return map;
  }, [historyUsers]);

  // Both edit paths stage the change behind the shared confirmation dialog —
  // the mutation only fires once the practitioner confirms (optionally
  // leaving a note).
  const handleTypeChange = useCallback(
    (newType: string) => {
      if (isViewer) return;
      setPendingEdit({
        fieldLabel: BANK_TRANSACTION_FIELD_LABELS.transactionType,
        onConfirm: (note) =>
          updateField({
            entryId: txn._id as Id<"bankTransactionEntries">,
            field: "transactionType",
            value: newType,
            note,
          }),
      });
    },
    [txn._id, updateField, isViewer],
  );

  const handleCounterpartyBlur = useCallback(() => {
    if (isViewer) return;
    const trimmed = counterpartyInput.trim();
    if (trimmed === (txn.counterpartyName ?? "")) return;
    setPendingEdit({
      fieldLabel: BANK_TRANSACTION_FIELD_LABELS.counterpartyName,
      onConfirm: (note) =>
        updateField({
          entryId: txn._id as Id<"bankTransactionEntries">,
          field: "counterpartyName",
          value: trimmed || undefined,
          note,
        }),
    });
  }, [txn._id, txn.counterpartyName, counterpartyInput, updateField, isViewer]);

  const handleCounterpartyKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur();
    }
  }, []);

  const extractionType = txn.extractionTransactionType ?? txn.transactionType;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex justify-end"
        style={{ background: "rgba(0,0,0,0.3)" }}
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        {/* Panel */}
        <div className="h-full w-full max-w-md overflow-y-auto border-l bg-background shadow-lg animate-in slide-in-from-right">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
            <div className="min-w-0">
              <p className="font-mono text-xs text-muted-foreground">
                {txn.date}
              </p>
              <h3 className="mt-0.5 truncate text-lg font-semibold">
                {txn.counterpartyName || txn.description}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="ml-3 shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>

          <div className="space-y-6 px-5 py-4">
            {/* Payment details */}
            <div>
              <p className="mb-3 text-[12px] font-medium uppercase tracking-wider text-foreground">
                Payment
              </p>
              <div className="space-y-0 divide-y">
                <DetailRow label="Date" value={txn.date} />
                <DetailRow
                  label="Amount"
                  value={
                    <span className="font-semibold tabular-nums">
                      {txn.direction === "credit" ? "+" : "-"}
                      {fmt(txn.amount)}
                    </span>
                  }
                />
                <DetailRow label="Direction" value={txn.direction} />
                <DetailRow
                  label="Description"
                  value={
                    <span className="font-mono text-xs">{txn.description}</span>
                  }
                />
                {txn.reference && (
                  <DetailRow label="Reference" value={txn.reference} />
                )}
                {txn.accountInstitution && (
                  <DetailRow label="Bank" value={txn.accountInstitution} />
                )}
                {txn.accountNumber && (
                  <DetailRow label="Account" value={txn.accountNumber} />
                )}
                {txn.balanceAfter != null && (
                  <DetailRow
                    label="Balance after"
                    value={
                      <span className="tabular-nums">
                        {fmt(txn.balanceAfter)}
                      </span>
                    }
                  />
                )}
              </div>
            </div>

            {/* Extraction classification (read-only) */}
            <div>
              <p className="mb-3 text-[12px] font-medium uppercase tracking-wider text-foreground">
                Extraction Classification
              </p>
              <div className="space-y-0 divide-y">
                <DetailRow
                  label="Type"
                  value={
                    <Badge
                      variant="soft"
                      size="sm"
                      color={TYPE_BADGE_COLOR[extractionType] ?? "gray"}
                    >
                      {TRANSACTION_TYPES.find((t) => t.value === extractionType)
                        ?.label ?? extractionType}
                    </Badge>
                  }
                />
                <DetailRow
                  label="Counterparty"
                  value={
                    txn.extractionCounterpartyName ??
                    txn.counterpartyName ??
                    "-"
                  }
                />
              </div>
            </div>

            {/* Agent classification (editable) */}
            <div>
              <p className="mb-3 text-[12px] font-medium uppercase tracking-wider text-foreground">
                Agent Classification
              </p>

              {/* Agent Category */}
              <div className="mb-3">
                <label className="mb-1.5 block text-xs text-muted-foreground">
                  Agent Category
                </label>
                <Select
                  value={txn.transactionType}
                  onValueChange={handleTypeChange}
                  disabled={isViewer}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSACTION_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Agent Counterparty */}
              <div>
                <label className="mb-1.5 block text-xs text-muted-foreground">
                  Agent Counterparty
                </label>
                <input
                  type="text"
                  value={counterpartyInput}
                  onChange={(e) => setCounterpartyInput(e.target.value)}
                  onBlur={handleCounterpartyBlur}
                  onKeyDown={handleCounterpartyKeyDown}
                  disabled={isViewer}
                  placeholder="Enter counterparty name..."
                  className="w-full rounded-lg border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                />
              </div>

              {txn.isManualOverride && (
                <p className="mt-2 flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                  >
                    <path d="M6 1a5 5 0 100 10A5 5 0 006 1zm.5 7.5h-1v-1h1v1zm0-2h-1v-3h1v3z" />
                  </svg>
                  Manually overridden by practitioner
                </p>
              )}
            </div>

            {/* Vouching (MOR-574) — bank ↔ ledger match + ledger characterisation */}
            {vouchingActive && vouchStatus && (
              <div>
                <p className="mb-3 text-[12px] font-medium uppercase tracking-wider text-foreground">
                  Vouching
                </p>
                <div className="flex items-center justify-between gap-3">
                  <VouchBadge status={vouchStatus} />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  {VOUCH_CONFIG[vouchStatus].tooltip}
                </p>

                {/* Ledger characterisation carried across on a match — the GL
                    account is the director's contemporaneous characterisation
                    of the payment, which drives the recovery type. */}
                {ledgerCharacterisation && (
                  <div className="mt-3 space-y-1.5 rounded-lg border bg-muted/30 p-3">
                    <p className="text-xs font-medium text-foreground">
                      Ledger (Xero) says
                    </p>
                    {ledgerCharacterisation.account && (
                      <p className="text-xs text-foreground">
                        Account:{" "}
                        <strong>{ledgerCharacterisation.account}</strong>
                      </p>
                    )}
                    {ledgerCharacterisation.description && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-mono">
                          {ledgerCharacterisation.description}
                        </span>
                      </p>
                    )}
                    {ledgerCharacterisation.direction && (
                      <p className="text-xs text-muted-foreground">
                        Ledger direction: {ledgerCharacterisation.direction}
                      </p>
                    )}
                    <p className="flex items-start gap-1 pt-0.5 text-[11px] text-amber-600 dark:text-amber-400">
                      <span aria-hidden>⚠</span>
                      <span>
                        The director's recorded characterisation — surface as
                        fact only if the interpretation can be articulated.
                      </span>
                    </p>
                  </div>
                )}

                {/* Practitioner override */}
                {!isViewer && jobId && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      variant={
                        vouchOverride?.action === "force_vouched"
                          ? "secondary"
                          : "outline"
                      }
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleVouchOverride("force_vouched")}
                    >
                      Mark vouched
                    </Button>
                    <Button
                      variant={
                        vouchOverride?.action === "force_unmatched"
                          ? "secondary"
                          : "outline"
                      }
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleVouchOverride("force_unmatched")}
                    >
                      Mark unmatched
                    </Button>
                    {vouchOverride && (
                      <button
                        onClick={() => handleVouchOverride("clear")}
                        className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Clear override
                      </button>
                    )}
                  </div>
                )}
                {vouchOverride && (
                  <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                    Vouch status manually overridden by practitioner.
                  </p>
                )}
              </div>
            )}

            {/* Source document */}
            {txn.sourceFileName && (
              <div>
                <p className="mb-3 text-[12px] font-medium uppercase tracking-wider text-foreground">
                  Source
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (txn.sourceDocumentId) {
                      setViewingDocumentId(
                        txn.sourceDocumentId as Id<"documents">,
                      );
                    }
                  }}
                  className="group flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      className="text-muted-foreground"
                    >
                      <path d="M4 1h6l4 4v10H4z" />
                      <path d="M10 1v4h4" />
                    </svg>
                  </div>
                  <p className="flex-1 truncate text-sm">
                    {txn.sourceFileName}
                  </p>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    className="shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground"
                  >
                    <path d="M6 4l4 4-4 4" />
                  </svg>
                </button>
              </div>
            )}

            {/* Notes */}
            {txn.notes && (
              <div>
                <p className="mb-3 text-[12px] font-medium uppercase tracking-wider text-foreground">
                  Notes
                </p>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {txn.notes}
                </p>
              </div>
            )}

            {/* History */}
            <div className="border-t pt-4">
              <ChangeHistory
                history={txn.changeHistory}
                usersById={historyUsersById}
                formatters={BANK_TRANSACTION_FIELD_FORMATTERS}
                fieldLabels={BANK_TRANSACTION_FIELD_LABELS}
              />
            </div>
          </div>
        </div>
      </div>

      <DocumentDetail
        documentId={viewingDocumentId}
        isViewer
        open={viewingDocumentId !== null}
        onClose={() => setViewingDocumentId(null)}
      />

      {/* Confirmation gate for every inline edit in this panel. Closing
          without confirming re-syncs the counterparty input to the stored
          value so a cancelled rename doesn't linger in the field. */}
      <ConfirmEditDialog
        edit={pendingEdit}
        onClose={() => {
          setPendingEdit(null);
          setCounterpartyInput(txn.counterpartyName ?? "");
        }}
      />
    </>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex justify-between py-2.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}
